/*
  What is left here is URL *building*, not URL state.

  The query-param reader and writer that lived here went with the old pagination in task
  11: table state — page, rows per page, order, filter — lives in the table controller and
  is never written to the query string (D23).
*/

/*
  Encode the one character that changes a slug's *shape* inside a path: the slash.

  An unencoded slash splits one path segment into two, so the URL lands on a different
  route entirely. A card numbered `1/2/3` misses `:gameCode/:setName/:number` and falls
  through to `:gameCode/:setName/:number/:finalGrade`, matching as number `1%2F2`, grade
  `3` — a plausible wrong page, never an error, which is why it went unnoticed. The same
  is true of a set named `Fate/Zero`. Production carries both: card numbers like
  `019/262 // T01/T04` (Star Wars Unlimited, double-sided) and 39 set names with a slash.

  The replacement is global. It used to take a string argument, which encodes only the
  FIRST occurrence, so anything with two slashes stayed broken.

  Deliberately NOT encodeURIComponent. That would also encode spaces, and set names
  legitimately contain them ("Base Set"), so every existing card and set URL would change
  shape. Encoding only the slash keeps the output byte-identical for any slug carrying at
  most one — i.e. this only ever changes URLs that are already broken today.
*/
export const sanitizeURIPathSegment = (segment: string | undefined) => {
  if (!segment) {
    return segment;
  }
  return segment.replace(/\//g, '%2F');
}

/*
  The same, plus `&`, for the `number` segment.

  `&` is a legal sub-delim in a path segment (RFC 3986) and react-router never splits on
  it, so encoding it fixes nothing — no production card number contains one. It stays only
  because every `number` link ever emitted has been built this way, and dropping it would
  change working URLs for no gain. That is also why it is NOT extended to set names: 392
  production set names contain `&` and their URLs work today, so encoding them now would
  change 392 working URLs to fix none.

  routing.test.ts pins both rules against the old implementation.
*/
export const sanitizeURISlug = (slug: string | undefined) => {
  if (!slug) {
    return slug;
  }
  return sanitizeURIPathSegment(slug)!.replace(/&/g, '%26');
}
